/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : FileUpload.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  LinearProgress,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { componentStyle } from "../../config/appTheme";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions/actionHandler";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

const FileUpload = ({
  multiple = false,
  accept = [],
  maxSize = 1024 * 1024 * 1024, // 1GB default
  disabled = false,
  dialogMode = false,
  open = false,
  onClose,
  beforeUpload,
  onUploadSuccess,
  onUploadError,
}) => {
  const inputRef = useRef();
  const [files, setFiles] = useState([]);

  const dispatch = useDispatch();

  const validateFile = (file) => {
    if (file.size > maxSize) return "File exceeds max size";
    if (accept.length > 0) {
      const valid = accept.some((ext) => file.name.toLowerCase().endsWith(ext));
      if (!valid) return "Invalid file type";
    }
    return null;
  };

  const updateFile = (index, updates) => {
    setFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...updates } : f)),
    );
  };

  const uploadSimple = async (file, index) => {
    try {
      updateFile(index, { status: "Preparing..." });

      const startResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            api_name: "/FSM/v1/assets/upload/get/presigned-url",
            https_method: "POST",
            params: {
              file_name: file.name,
              content_type: file.type,
              size: file.size,
              asset_folder: "report-templates",
            },
            show_toast: false,
          }),
        ),
      );

      const { url, key } = startResp.data;

      await axios.put(url, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded / e.total) * 100);
          updateFile(index, { progress: percent });
        },
      });

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            api_name: "/FSM/v1/assets/upload/create/record",
            https_method: "POST",
            params: {
              key,
              file_name: file.name,
              size: file.size,
              content_type: file.type,
            },
            show_toast: true,
          }),
        ),
      );

      updateFile(index, { progress: 100, status: "success" });

      if (onUploadSuccess) onUploadSuccess({ key, file });
    } catch (err) {
      updateFile(index, { status: "error", error: "Upload failed" });
      if (onUploadError) onUploadError(err);
    }
  };

  const uploadMultipart = async (file, index) => {
    try {
      updateFile(index, { status: "Starting multipart..." });

      const startResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            api_name: "/FSM/v1/assets/upload/multipart/start",
            https_method: "POST",
            params: {
              filename: file.name,
              content_type: file.type,
            },
            show_toast: false,
          }),
        ),
      );

      const { upload_id, key } = startResp.data;

      const totalParts = Math.ceil(file.size / CHUNK_SIZE);

      const partsResp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            api_name: "/FSM/v1/assets/upload/multipart/parts",
            https_method: "POST",
            params: {
              key,
              upload_id,
              part_count: totalParts,
            },
            show_toast: false,
          }),
        ),
      );

      const partUrls = partsResp.data.urls;
      const parts = [];

      for (let i = 0; i < totalParts; i++) {
        const startByte = i * CHUNK_SIZE;
        const endByte = Math.min(startByte + CHUNK_SIZE, file.size);
        const blob = file.slice(startByte, endByte);

        const res = await axios.put(partUrls[i], blob, {
          headers: { "Content-Type": file.type },
          onUploadProgress: (e) => {
            const percent = Math.round(
              ((i * CHUNK_SIZE + e.loaded) / file.size) * 100,
            );
            updateFile(index, { progress: percent });
          },
        });

        parts.push({
          ETag: res.headers.etag.replaceAll('"', ""),
          PartNumber: i + 1,
        });
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            api_name: "/FSM/v1/assets/upload/multipart/complete",
            https_method: "POST",
            params: {
              key,
              upload_id,
              parts,
              filename: file.name,
              size: file.size,
              content_type: file.type,
            },
            show_toast: true,
          }),
        ),
      );

      updateFile(index, { progress: 100, status: "success" });

      if (onUploadSuccess) onUploadSuccess({ key, file });
    } catch (err) {
      updateFile(index, { status: "error", error: "Multipart failed" });
      if (onUploadError) onUploadError(err);
    }
  };

  const handleFiles = (selectedFiles) => {
    const newFiles = Array.from(selectedFiles).map((file) => {
      const error = validateFile(file);
      return {
        file,
        progress: 0,
        status: error ? "error" : "uploading",
        error,
      };
    });

    setFiles(multiple ? [...files, ...newFiles] : newFiles);

    for (const [index, fileObj] of newFiles.entries()) {
      if (fileObj.error) continue;
      (async () => {
        try {
          if (beforeUpload) {
            updateFile(index, { status: "Validating..." });

            const result = await beforeUpload(fileObj.file);

            if (!result?.isValid) {
              updateFile(index, {
                status: "error",
                error: result?.message || "Validation failed",
              });
              return;
            }
          }

          updateFile(index, { status: "Uploading..." });

          if (fileObj.file.size <= 10 * 1024 * 1024) {
            await uploadSimple(fileObj.file, index);
          } else {
            await uploadMultipart(fileObj.file, index);
          }
        } catch (err) {
          console.error("Upload validation error:", err);
          updateFile(index, {
            status: "error",
            error: "Validation failed",
          });
        }
      })();
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const UploadContent = (
    <>
      <Typography
        shrink
        textAlign="start"
        htmlFor="select-field"
        sx={{
          mb: 0.5,
          ...componentStyle.font.cardHeadder,
        }}
      >
        Upload files <span style={{ color: "red" }}>*</span>
      </Typography>

      <Typography
        shrink
        textAlign="start"
        htmlFor="select-field"
        sx={{
          mb: 0.5,
          ...componentStyle.font.form.textFieldDescription,
        }}
      >
        DOCX. Max size of {maxSize / (1024 * 1024 * 1024)}G{" "}
      </Typography>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (!disabled) handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !disabled && inputRef.current.click()}
      >
        <input
          hidden
          type="file"
          ref={inputRef}
          multiple={multiple}
          accept={accept.join(",")}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "start", mt: 2 }}>
        <CustomButtonV2
          type="submit"
          btnName="Choose file"
          handleOnClick={(e) => {
            e.stopPropagation();
            inputRef.current.click();
          }}
        />
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {files.map((item, index) => (
            <Box key={`${item.file?.name}-${index}`} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">{item.file.name}</Typography>
                <IconButton size="small" onClick={() => removeFile(index)}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {item.status === "error" ? (
                <Typography variant="caption" color="error">
                  {item.error}
                </Typography>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={item.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    mt: 1,
                    backgroundColor: "#26890D",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      )}
    </>
  );

  if (dialogMode) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: ".8rem",
            position: "relative",
          },
        }}
      >
        <DialogContent sx={{ position: "relative", pt: 4, minWidth: "400px" }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#53565A",
            }}
          >
            <CloseIcon />
          </IconButton>

          {UploadContent}
        </DialogContent>
        <DialogActions>
          <CustomButtonV2
            type="cancel"
            btnName="Close"
            handleOnClick={onClose}
          />
        </DialogActions>
      </Dialog>
    );
  }

  return <Box>{UploadContent}</Box>;
};

FileUpload.propTypes = {
  multiple: PropTypes.bool,
  accept: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
  disabled: PropTypes.bool,
  dialogMode: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  beforeUpload: PropTypes.func,
};

export default FileUpload;
